/**
 * Shared WhatsApp template display helpers.
 *
 * Extracted from pages/settings/whatsapp-cloud/index.tsx (FE-01) so the
 * WhatsApp Inbox's template picker (FE-14) can render templates the same way
 * the settings page does, without duplicating the status-color mapping,
 * date formatting, or {{n}} placeholder highlighting.
 *
 * Plain .ts (no JSX) — renderTemplateBody uses React.createElement directly.
 */
import React from 'react';

export const TEMPLATE_STATUS_BADGE: Record<string, string> = {
  APPROVED: 'bg-green-100 text-green-700',
  PENDING: 'bg-amber-100 text-amber-700',
  REJECTED: 'bg-red-100 text-red-700',
  PAUSED: 'bg-gray-100 text-gray-600',
};

/** WhatsApp's own chat-background color — used everywhere a template or
 * message bubble is previewed, so every preview reads as "real WhatsApp." */
export const WA_BUBBLE_BG = '#e5ded8';

export const formatTemplateDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const PLACEHOLDER_RE = /(\{\{\s*\d+\s*\}\})/g;
const IS_PLACEHOLDER_RE = /\{\{\s*\d+\s*\}\}/;

/** Render {{1}} placeholders as highlighted chips inside a template body preview. */
export const renderTemplateBody = (body: string): React.ReactNode =>
  body.split(PLACEHOLDER_RE).map((part, i) =>
    IS_PLACEHOLDER_RE.test(part)
      ? React.createElement(
          'span',
          {
            key: i,
            className: 'inline-block px-1 mx-0.5 rounded bg-brandColor/10 text-brandColor font-medium',
          },
          part,
        )
      : React.createElement(React.Fragment, { key: i }, part),
  );
